import React from 'react';

/**
 * The last thing between a thrown error and a white screen.
 *
 * Two different failures used to land the same way — nothing on screen, no
 * explanation, and a user who assumes the app is down:
 *
 *  1. A stale code chunk. Every build names its files with a content hash, so
 *     after a deploy the old names stop existing. A tab left open since before
 *     the deploy asks for chunk 297, the server answers with index.html, and
 *     the browser throws "Unexpected token '<'" trying to run HTML as
 *     JavaScript. Nothing is wrong with the app; the page simply predates it.
 *     That one is fixed by reloading, so it reloads itself — once.
 *
 *  2. Any other render error, which reloading will not fix. Repeating the
 *     reload there would be a loop, so it stops and says what happened.
 *
 * The one-shot flag lives in sessionStorage so a genuine crash on the fresh
 * page cannot bounce the user round forever.
 */

const RELOAD_KEY = 'tychi:chunk-reloaded';

const isChunkError = (error) => {
  const name = String(error?.name || '');
  const msg = String(error?.message || '');
  return name === 'ChunkLoadError'
    || /Loading chunk [\d]+ failed/i.test(msg)
    || /Loading CSS chunk/i.test(msg)
    // The symptom when the server answers a missing .js with index.html.
    || /Unexpected token '</.test(msg)
    || /expected expression, got '<'/i.test(msg);
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (isChunkError(error) && !sessionStorage.getItem(RELOAD_KEY)) {
      try { sessionStorage.setItem(RELOAD_KEY, '1'); } catch { /* private mode */ }
      window.location.reload();
      return;
    }
    // Kept out of the UI — the message is for whoever reads the console, the
    // card below is for whoever is trying to get their work done.
    console.error('[tychi] render error:', error);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkError(error);
    return (
      <div style={{
        minHeight: '60vh', display: 'grid', placeItems: 'center', padding: 24,
      }}>
        <div className="card hrms-card" style={{ maxWidth: 460, textAlign: 'center' }}>
          <h2 className="card-title" style={{ justifyContent: 'center' }}>
            {chunk ? 'A new version is available' : 'Something went wrong'}
          </h2>
          <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6 }}>
            {chunk
              ? 'This page was loaded before the last update. Reload to pick up the new version — nothing you have saved is affected.'
              : 'This screen could not be drawn. Reloading usually clears it. If it keeps happening, tell IT what you were doing.'}
          </p>
          <div className="form-actions" style={{ justifyContent: 'center' }}>
            <button type="button" className="btn btn-primary"
              onClick={() => {
                try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* ignore */ }
                window.location.reload();
              }}>
              <i className="ti ti-refresh" /> Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
